import { useId, type CSSProperties, type ReactElement, type ReactNode } from "react";
import {
  containerCapacity,
  fittedModsDeep,
  isTrueMagazine,
  loadedRounds,
  loadedRoundsKnown,
  magCountKnown,
  usedCells,
  type ItemDef,
  type ItemInstance,
  type ItemKind,
  type Rotation,
} from "@rietz/shared";
import { useHud } from "../store.js";
import {
  ITEM_ATLAS_COLUMNS,
  ITEM_ATLAS_COORDS,
  ITEM_ATLAS_ROWS,
  ITEM_ATLAS_URL,
} from "./itemAtlas.generated.js";
import { useItemPortrait } from "./itemPortrait.js";

/**
 * What one item looks like. `TileBody` fills whatever box it is put in — a grid
 * cell span, an equipment slot, the drag ghost — so one piece of markup
 * describes an item everywhere. Colour coding is by KIND and quiet: translucent
 * washes over the dark grid, since forty loud rectangles are as unreadable as
 * forty grey ones.
 *
 * GUNS AND THEIR PARTS ARE RENDERED, NOT PAINTED: a weapon's artwork is a side
 * view of `buildWeapon(def, mods)` — the gun the player actually built, how you
 * tell your three carbines apart — and a part's is `buildPart(def)`, the very
 * component the gun would wear. Everything else comes from the item atlas. See
 * `itemPortrait.ts`.
 */

interface KindStyle {
  fill: string;
  line: string;
}

/** Brass, gauze, gunmetal, plate, canvas, tat. */
const KIND_STYLE: Record<ItemKind, KindStyle> = {
  ammo: { fill: "rgba(158, 122, 62, 0.30)", line: "rgba(206, 170, 104, 0.55)" },
  med: { fill: "rgba(206, 214, 218, 0.20)", line: "rgba(214, 108, 100, 0.60)" },
  weapon: { fill: "rgba(84, 112, 142, 0.32)", line: "rgba(140, 172, 204, 0.55)" },
  armor: { fill: "rgba(126, 130, 136, 0.30)", line: "rgba(178, 182, 190, 0.50)" },
  container: { fill: "rgba(104, 116, 74, 0.32)", line: "rgba(160, 174, 112, 0.52)" },
  loot: { fill: "rgba(150, 126, 92, 0.28)", line: "rgba(198, 172, 130, 0.50)" },
  // A cooler, dimmer gunmetal than the weapon's, so a scope reads as a PART of
  // the gun family without being mistaken for a gun at a glance.
  attachment: { fill: "rgba(84, 112, 142, 0.20)", line: "rgba(140, 172, 204, 0.40)" },
  // Olive drab with a warning-orange line: ordnance, not a tin of beans.
  grenade: { fill: "rgba(96, 104, 66, 0.32)", line: "rgba(214, 150, 70, 0.60)" },
};

export interface TileBodyProps {
  def: ItemDef;
  item: ItemInstance;
  /** The source tile while its own ghost is being dragged around. */
  faded?: boolean;
  /**
   * Washed out but still live — the trader screen's "won't buy this", at the
   * same opacity as an unaffordable offer. The tile keeps its handlers.
   */
  dimmed?: boolean;
  /** Optional compact readout sharing the tile's bottom row with its native
   *  stack/charge badge. Trader shelves use it for the price. */
  footer?: ReactNode;
  /** Inventory instances are grabbed; catalogue instances are clicked. */
  cursor?: CSSProperties["cursor"];
  /**
   * Picked out by the host screen and still lying where it is — the counter's
   * staged items. A warm ring, not a wash: "chosen", not "unavailable". Drop
   * feedback outranks it.
   */
  marked?: boolean;
  /** Compatibility feedback while a loose part is held over this weapon. */
  modDrop?: "valid" | "invalid";
  /** Placement feedback while an item is held over this container. */
  containerDrop?: "valid" | "invalid";
  /** Compact equipment/mod slots can let the artwork identify the item. */
  iconOnly?: boolean;
  /** Grid orientation. Rotates the artwork with the item's occupied cells. */
  rot?: Rotation;
}

/** Stable identity for the hook's dependencies on every tile that is not a gun. */
const EMPTY_MODS: readonly string[] = Object.freeze([]);

export function TileBody(props: TileBodyProps): ReactElement {
  const { def, item } = props;
  const clipId = useId();
  const style = KIND_STYLE[def.kind];
  // The same derivation the Werkbank's preview uses, DEEP, so the tile and the
  // bench build from one parts list.
  const mods = def.kind === "weapon" ? fittedModsDeep(def, item).map((f) => f.def.id) : EMPTY_MODS;
  // The two kinds whose picture is a render. `mods` stays weapon-only: a part
  // that hosts parts draws itself; its glass has a tile of its own.
  const rendered = def.kind === "weapon" || def.kind === "attachment";
  const portrait = useItemPortrait(rendered ? def.kind : null, def.id, mods, def.w, def.h);
  const artwork = tileArtwork(def, portrait, props.rot);

  return (
    <div
      data-weapon-iid={def.kind === "weapon" ? item.iid : undefined}
      data-container-iid={def.kind === "container" ? item.iid : undefined}
      style={{
        position: "absolute",
        inset: 0,
        boxSizing: "border-box",
        padding: "3px 4px",
        borderRadius: 3,
        border: `1px solid ${style.line}`,
        background: style.fill,
        overflow: "hidden",
        opacity: props.faded === true ? 0.3 : props.dimmed === true ? 0.38 : 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        cursor: props.cursor ?? "grab",
        outline: (() => {
          const feedback = props.modDrop ?? props.containerDrop;
          if (feedback === undefined) {
            return props.marked === true ? "2px solid rgba(238, 205, 148, 0.95)" : "none";
          }
          return `2px solid ${
            feedback === "valid" ? "rgba(140, 220, 148, 0.92)" : "rgba(232, 104, 92, 0.92)"
          }`;
        })(),
        outlineOffset: -2,
      }}
    >
      {artwork === null ? null : (
        <svg
          aria-hidden="true"
          viewBox={artwork.viewport.viewBox}
          preserveAspectRatio="xMidYMid meet"
          style={{
            position: "absolute",
            inset: 0,
            display: "block",
            width: "100%",
            height: "100%",
            opacity: artwork.opacity,
            pointerEvents: "none",
            userSelect: "none",
            overflow: "hidden",
          }}
        >
          {/* The viewBox only maps the artwork rect onto the tile; when their
              aspect ratios differ, the spare viewport would show neighbouring
              atlas sprites. The clip pins the image to the artwork rect. */}
          <clipPath id={clipId}>
            <rect
              x={artwork.viewport.x}
              y={artwork.viewport.y}
              width={artwork.viewport.width}
              height={artwork.viewport.height}
            />
          </clipPath>
          <g clipPath={`url(#${clipId})`}>
            <image
              href={artwork.href}
              x={0}
              y={0}
              width={artwork.imageWidth}
              height={artwork.imageHeight}
              transform={artwork.viewport.transform}
            />
          </g>
        </svg>
      )}

      {props.iconOnly === true ? null : (
        <div
          style={{
            position: "relative",
            zIndex: 1,
            fontSize: 10,
            lineHeight: 1.1,
            letterSpacing: "-0.01em",
            color: "#eef3f8",
            minWidth: 0,
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textShadow: "0 1px 2px #000, 0 0 4px #000",
          }}
        >
          {def.shortName}
        </div>
      )}

      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: props.footer === undefined ? "flex-end" : "space-between",
          gap: 2,
        }}
      >
        {props.footer}
        {/* A compact slot tile normally shows artwork and nothing else — there is
            no room for a label, and a stack count on a part that cannot stack
            would be noise. A TRUE MAGAZINE is the exception, because its badge is
            not decoration: the rounds in the box are the whole reason to pick
            THIS magazine out of the three in the rig, and the mod bench's slot
            tile is now where a loaded magazine is looked at. */}
        {props.iconOnly === true && !isTrueMagazine(def) ? null : (
          <Badge def={def} item={item} />
        )}
      </div>

      <DurabilityBar def={def} item={item} />
    </div>
  );
}

type AtlasRect = readonly [x: number, y: number, width: number, height: number];

/** One `<image>` and the sub-rect of it the tile shows, whatever the source. */
interface TileArtwork {
  href: string;
  /** The image's own extent in viewBox units — a whole atlas, or one portrait. */
  imageWidth: number;
  imageHeight: number;
  opacity: number;
  viewport: ArtworkViewport;
}

/**
 * Where the tile's picture comes from: a rendered model, or the painted atlas.
 * They differ only in the image and its rect's units (atlas cells vs the def's
 * grid footprint); everything downstream is unit-agnostic. `null` means draw no
 * picture — the label and badge carry the tile.
 */
function tileArtwork(
  def: ItemDef,
  portrait: string | null,
  rot: Rotation | undefined,
): TileArtwork | null {
  if (def.kind === "weapon" || def.kind === "attachment") {
    if (portrait === null) return null;
    return {
      href: portrait,
      imageWidth: def.w,
      imageHeight: def.h,
      // A lit render, not a painted icon: dimming a photograph of the thing
      // only
      // makes it harder to see.
      opacity: 1,
      viewport: artworkViewport([0, 0, def.w, def.h], rot),
    };
  }

  const icon = ITEM_ATLAS_COORDS[def.id];
  if (icon === undefined) return null;
  return {
    href: ITEM_ATLAS_URL,
    imageWidth: ITEM_ATLAS_COLUMNS,
    imageHeight: ITEM_ATLAS_ROWS,
    opacity: 0.86,
    viewport: artworkViewport(icon, rot),
  };
}

interface ArtworkViewport {
  viewBox: string;
  x: number;
  y: number;
  width: number;
  height: number;
  transform: string | undefined;
}

/** Fit the visible artwork, rather than its square atlas cell, into the tile. */
function artworkViewport(rect: AtlasRect, rot: Rotation | undefined): ArtworkViewport {
  const [x, y, width, height] = rect;
  if (rot !== 1) {
    return { viewBox: `${x} ${y} ${width} ${height}`, x, y, width, height, transform: undefined };
  }

  const centerX = x + width / 2;
  const centerY = y + height / 2;
  const left = centerX - height / 2;
  const top = centerY - width / 2;
  return {
    viewBox: `${left} ${top} ${height} ${width}`,
    x: left,
    y: top,
    width: height,
    height: width,
    transform: `rotate(90 ${centerX} ${centerY})`,
  };
}

/**
 * The bottom-right number, whatever it means for this kind — one slot, five
 * meanings, none of which can coexist. Two are RAID-CONDITIONAL: in a raid a
 * magazine not looked into since it was fired reads "?", the same withholding
 * the HUD does. Read off the SCREEN rather than passed in.
 */
function Badge(props: { def: ItemDef; item: ItemInstance }): ReactElement | null {
  const { def, item } = props;
  // Unconditional and before every early return, as a hook must be — `Badge` is
  // a real component.
  const inRaid = useHud((s) => s.screen === "raid");

  // How full the box is, "used/total" like the panel headers. Only when there
  // is
  // something in it — an empty container has no `contents` key, and a WORN bag
  // never has contents (its capacity is hoisted onto the inventory).
  if (def.kind === "container") {
    const contents = item.contents;
    if (contents === undefined) return null;
    return (
      <span style={badgeStyle("#dbe7c8")}>
        {usedCells(contents)}/{containerCapacity(def)}
      </span>
    );
  }
  if (def.kind === "med") {
    const left = item.durability ?? def.charges;
    return (
      <span style={badgeStyle("#f0dcd8")}>
        {left}/{def.charges}
      </span>
    );
  }
  if (def.kind === "weapon") {
    // What the gun holds is what the box IN it holds (`loadedRounds`; 0 for an
    // empty well). An EMPTY well is always known, so that 0 survives the
    // raid-only "?".
    return (
      <span style={badgeStyle("#d8e6f4")}>
        {inRaid && !loadedRoundsKnown(def, item) ? "?" : loadedRounds(def, item)}
      </span>
    );
  }
  // A MAGAZINE over its capacity — and "0/30" IS the reading for an empty one,
  // unlike the box lid: the count is the only difference between two identical
  // tiles, and R spends a whole magazine. FITTED magazines draw this badge too.
  if (isTrueMagazine(def)) {
    return (
      <span style={badgeStyle("#d8e6f4")}>
        {inRaid && !magCountKnown(def, item) ? "?" : (item.mag ?? 0)}/{def.magCapacity}
      </span>
    );
  }
  if (item.stack > 1) return <span style={badgeStyle("#f2e8d6")}>×{item.stack}</span>;
  return null;
}

function badgeStyle(color: string): CSSProperties {
  return {
    font: "600 10px/1 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
    color,
    textShadow: "0 1px 2px rgba(0, 0, 0, 0.9)",
  };
}

/**
 * Two pixels of remaining plate — or gun — red as it goes. The two kinds that
 * wear out get the SAME bar: one number, one meaning. Nothing else draws a bar.
 */
function DurabilityBar(props: { def: ItemDef; item: ItemInstance }): ReactElement | null {
  const { def, item } = props;
  if (def.kind !== "armor" && def.kind !== "weapon") return null;
  // The denominator is the DEF's number, never the repaired-down ceiling: a bar
  // that filled up again would hide what the repair cost.
  const defMax = def.maxDurability > 0 ? def.maxDurability : 1;
  const frac = Math.max(0, Math.min(1, (item.durability ?? item.maxDurability ?? defMax) / defMax));
  return (
    <div
      style={{
        position: "absolute",
        left: 3,
        right: 3,
        bottom: 2,
        height: 2,
        background: "rgba(0, 0, 0, 0.45)",
      }}
    >
      <div
        style={{
          width: `${(frac * 100).toFixed(1)}%`,
          height: "100%",
          // Same reading as the limb bars: green is fine, red is about to fail.
          background: `hsl(${(120 * frac * frac).toFixed(0)}, 58%, 48%)`,
        }}
      />
    </div>
  );
}
